import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { hivesApi } from "../services/hivesApi";
import { exportApi } from "../services/exportApi";
import { useFeatureTheme } from "../context/FeatureThemeContext";
import { useToast } from "../context/ToastContext";
import { useSelection } from "../hooks/useSelection";
import { Card } from "../components/common/Card";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { byUrgency, lastCheckedCell, nextCheckCell, urgencyTone } from "../components/hives/hiveUrgency";
import { withdrawalBadgeParts } from "../services/hiveActions";
import { DataTable } from "../components/common/DataTable";
import { groupByAccount, hasMultipleAccounts } from "../components/hives/accountGrouping";
import { HiveMap } from "../components/hives/HiveMap";
import { BatchActionBar } from "../components/hives/BatchActionBar";
import { BatchHistoryModal } from "../components/hives/BatchHistoryModal";
import { QrCodeLabel } from "../components/hives/QrCodeLabel";
import { PrintSiteReport } from "../components/print/PrintSiteReport";
import { LockedFeature } from "../components/common/LockedFeature";
import { ActionMenu } from "../components/common/ActionMenu";

// Active first and used as the default: the overwhelmingly common case is
// "show me the hives I actually keep". "All" sits at the end and now means
// literally all, archived included -- it previously defaulted the picker AND
// hid archived hives, so archiving one looked like destroying it.
const STATUSES = ["Active", "Dead", "Sold", "Merged", "Absconded", "Archived", "All"];

// Maps keys are configured (#179): the web key arrives at run time as an App
// Service setting via server.js's /config.js, and the Android/iOS keys are
// injected into mobile/.env by CI before the build. Set back to false if a
// key is ever revoked -- without one the SDK renders a blank grey panel and
// logs MissingKeyMapError, which reads as "broken" rather than "not set up".
const MAPS_ENABLED = true;


export function HiveListPage() {
  const { hasFeature, canChangeLists } = useFeatureTheme();
  const { notify } = useToast();
  const navigate = useNavigate();
  const selection = useSelection();

  const [hives, setHives] = useState(null);
  const [withdrawal, setWithdrawal] = useState([]);
  const [mapHives, setMapHives] = useState([]);
  const [view, setView] = useState("list");
  const [statusFilter, setStatusFilter] = useState("Active");
  // Arriving from a site card on the dashboard (#282). The grid promised
  // to take you to that yard, and landing on every hive you own instead
  // makes the click feel broken.
  const [searchParams, setSearchParams] = useSearchParams();
  const siteFilter = searchParams.get("site_id");
  const [historyOpen, setHistoryOpen] = useState(false);
  // Opened from the More menu now, so its state lives here (#327).
  const [printOpen, setPrintOpen] = useState(false);

  async function load() {
    // One filter, both views. The map used to be called with no arguments,
    // so it always showed Active hives no matter what the list was showing.
    const params = { status: statusFilter };
    if (siteFilter) params.site_id = siteFilter;
    try {
      const [listRes, mapRes] = await Promise.all([
        hivesApi.list(params),
        hivesApi.map(params),
      ]);
      // Default to [] rather than trusting the shape: an error body has no
      // .hives, and passing undefined down crashes the table and the map.
      setHives(listRes.hives || []);
      setMapHives(mapRes.hives || []);
      /* Separately and best-effort (#404): the badge is worth having and
         must never be the reason the hive list fails to load. Outside the
         Promise.all above for exactly that reason -- a rejection there
         takes the whole page to the catch. */
      hivesApi.withdrawal()
        .then((r) => setWithdrawal(r.withdrawal || []))
        .catch(() => {});
    } catch (err) {
      // Previously uncaught, so a failed load left the previous results on
      // screen with nothing to say they were stale -- the worst outcome,
      // because the page looks fine and is quietly lying.
      setHives([]);
      setMapHives([]);
      notify(err.apiMessage || "Could not load hives", "danger");
    }
  }

  useEffect(() => { load(); }, [statusFilter, siteFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function exportCsv() {
    try {
      await exportApi.hivesCsv();
    } catch (err) {
      notify(err.apiMessage || "Export failed", "danger");
    }
  }

  async function exportSitesCsv() {
    try {
      await exportApi.sitesCsv();
    } catch (err) {
      notify(err.apiMessage || "Export failed", "danger");
    }
  }

  async function exportJson() {
    try {
      const data = await exportApi.hivesJson();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "hives_export.json";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notify(err.apiMessage || "Export failed", "danger");
    }
  }

  async function exportZip() {
    try {
      await exportApi.allZip();
    } catch (err) {
      notify(err.apiMessage || "Export failed", "danger");
    }
  }

  const selectedHives = (hives || []).filter((h) => selection.selectedIds.has(h.hive_id));

  /*
   * The list itself, defined once and rendered either whole or per
   * account. Sorting stays INSIDE the group: worst-first still means
   * worst-first, it just means it within a section you are browsing.
   *
   * The #282 ranking is deliberately untouched by any of this. An urgent
   * issue at an account you help at still outranks a routine check at
   * your own on the dashboard -- sections are for BROWSING, and "needs
   * you now" is for deciding.
   */
  function HiveTable({ rows }) {
    return (
          <DataTable
            rows={byUrgency(rows)}
            loading={!hives}
            rowKey="hive_id"
            selection={selection}
            onRowClick={(r) => navigate(`/hives/${r.hive_id}`)}
            emptyTitle="No hives yet"
            emptyDescription="Hives are the centre of everything here — inspections, treatments and harvests all attach to one."
            emptyAction={canChangeLists === false ? null : <Button onClick={() => navigate("/hives/new")}>Add Hive</Button>}
            columns={[
              // Site first, and before the hive number on purpose: numbers
              // restart at 1 per site, so "Hive 1" identifies nothing on its
              // own. Reading site-then-number matches how a beekeeper says
              // it out loud, and how the detail page heading reads.
              { key: "site_name", header: "Site", render: (r) => r.site_name || "--" },
              {
                key: "hive_number",
                header: "Hive #",
                /* Honey that must not go in a jar yet (#404), on the row
                   you browse before you drive out. Beside the hive's own
                   number because it is a fact about the hive, not about
                   what you are doing to it -- and the form warnings stay,
                   for the keeper who arrives by "Next Hive" and never
                   reads a list. */
                render: (r) => {
                  const badge = withdrawalBadgeParts(withdrawal, r.hive_id);
                  return (
                    <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      {r.hive_number}
                      {badge && (
                        /* TWO LINES in a table cell, asked for from the
                           web app: one long pill crowded the row and read
                           worse the more a hive had to say. */
                        <Badge tone="danger">
                          <span style={{ display: "block", lineHeight: 1.25 }}>
                            <span style={{ display: "block" }}>{badge.lead}</span>
                            <span style={{ display: "block" }}>{badge.until}</span>
                          </span>
                        </Badge>
                      )}
                    </span>
                  );
                },
              },
              { key: "status", header: "Status", render: (r) => <Badge tone={r.status === "Active" ? "success" : "neutral"}>{r.status}</Badge> },
              /* WHY, and only where the question is being asked (#428).
                 Asked for as "when you look at the hive archive you see a
                 list of hives in the archived state and the reason WHY".
                 Only in the Archived view: an empty column on the active
                 list would be a column about a thing that has not
                 happened.
                 Blank for hives archived before this existed, and it stays
                 blank -- inventing a reason for one archived last year
                 would be a guess recorded as a fact. An em dash rather
                 than "Unknown", which would claim somebody looked. */
              ...(statusFilter === "Archived" ? [{
                key: "archived_reason",
                header: "Why",
                // The server strips the prefix and returns nothing for a
                // reasonless archive, so this renders what it is given.
                // Web never stripped and the phone did, which meant one
                // row read two ways -- and neither could tell a real
                // reason from the "Archived and removed from the active
                // list" fallback, which shipped as a badge on the phone.
                render: (r) => r.archived_reason
                  || <span style={{ color: "var(--color-text-muted)" }}>—</span>,
              }] : []),
              { key: "hive_type", header: "Type" },
              /*
               * Which hives need you, on the page you browse them from
               * (#206).
               *
               * This showed a bare date and nothing else, so a beekeeper
               * with forty hives had to do the arithmetic between today and
               * each printed date themselves -- which was the original
               * complaint. The dashboard card was fixed and this was not.
               *
               * Shared with that card rather than copied: two lists
               * disagreeing about which hives are overdue is this
               * codebase's most repeated bug in a new place.
               */
              { key: "last_checked", header: "Last Check", render: lastCheckedCell },
              { key: "next_check_due", header: "Next Check", render: nextCheckCell },
            ]}
            // Worst first. A list that puts the hive needing attention
            // wherever it happens to fall is a list you have to read all of.
            rowTone={urgencyTone}
          />
    );
  }

  return (
    <div>
      <PageHeader title="Hives">
        {/* Was EIGHT buttons in a row -- four of them the same job in
            different formats. docs/UI_ACTIONS.md rule 6: four visible,
            then a menu. See #327; this page was the worked example
            because it had 12 buttons, 4 primaries and NO modals, so
            every primary was competing on the page itself.

            Order is rule 8: secondary first, primary last, nearest where
            the eye finishes. */}
        <ActionMenu
          label="Export"
          items={[
            { label: "Hives (CSV)", onSelect: exportCsv },
            // Sites are their own file: address and coordinates belong
            // to the yard, and repeating them on every hive row would
            // make the hive export both larger and wrong to import.
            { label: "Sites (CSV)", onSelect: exportSitesCsv },
            { label: "Hives (JSON)", onSelect: exportJson },
            { label: "Everything (ZIP)", onSelect: exportZip },
          ]}
        />

        <ActionMenu
          label="More"
          items={[
            { label: "Print site report", onSelect: () => setPrintOpen(true) },
            { label: "Batch history", onSelect: () => setHistoryOpen(true) },
          ]}
        />

        {/* Stays VISIBLE rather than going in the menu: it appears only
            when rows are selected, so its presence IS the signal that a
            selection exists, and it names the count -- which rule 5
            requires of a bulk action. Hiding it in a menu would make a
            contextual action need discovering. */}
        {selectedHives.length > 0 && (
          <QrCodeLabel
            hives={selectedHives.map((h) => ({ hive_id: h.hive_id, hive_number: h.hive_number }))}
            trigger={<Button variant="secondary">Print QR Labels ({selectedHives.length})</Button>}
          />
        )}

        {canChangeLists === false ? null : <Button onClick={() => navigate("/hives/new")}>Add Hive</Button>}
      </PageHeader>

      <BatchHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <PrintSiteReport open={printOpen} onOpenChange={setPrintOpen} />

      {/* A filtered list that does not say it is filtered is a list that
          lies about how many hives you have -- the #197 mistake in a new
          place. Names the yard and offers the way out. */}
      {siteFilter && (
        <Card className="section-spacing">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <span>
              Showing <strong>{hives?.[0]?.site_name || "one site"}</strong> only.
            </span>
            <Button size="sm" variant="secondary" onClick={() => setSearchParams({})}>
              Show all hives
            </Button>
          </div>
        </Card>
      )}

      <Card
        title="Filters"
        className="section-spacing"
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <Button size="sm" variant={view === "list" ? "primary" : "secondary"} onClick={() => setView("list")}>List</Button>
            {/* Wrapped in a span so the tooltip still appears: a disabled button
                fires no mouse events, so title on the button itself is
                unreliable across browsers. */}
            <span title={MAPS_ENABLED ? undefined : "Map view is coming in a future update"}>
              <Button size="sm" disabled={!MAPS_ENABLED} variant={view === "map" ? "primary" : "secondary"} onClick={() => setView("map")}>Map</Button>
            </span>
          </div>
        }
      >
        <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <label>
            Status
            <select id="hive-list-page-status-filter" name="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="touch-target" style={{ display: "block" }}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <LockedFeature enabled={hasFeature("Treatments")} featureLabel="Treatment status filter" previewText="Filter hives by treatment status once Treatments is enabled.">
            <label>Treatment status<select id="hive-list-treatment-filter" name="treatment-filter" disabled className="touch-target" style={{ display: "block" }}><option>Any</option></select></label>
          </LockedFeature>
          <LockedFeature enabled={hasFeature("QueenManagement")} featureLabel="Queen age filter" previewText="Filter hives by queen age once Queen Management is enabled.">
            <label>Queen age<select id="hive-list-queen-age-filter" name="queen-age-filter" disabled className="touch-target" style={{ display: "block" }}><option>Any</option></select></label>
          </LockedFeature>
          <LockedFeature enabled={hasFeature("BloomCalendar")} featureLabel="Bloom status filter" previewText="Filter hives by nearby bloom status once Bloom Calendar is enabled.">
            <label>Bloom status<select id="hive-list-bloom-filter" name="bloom-filter" disabled className="touch-target" style={{ display: "block" }}><option>Any</option></select></label>
          </LockedFeature>
        </div>
      </Card>

      {/* DERIVED FROM THE SELECTION, not from the filter. Reported from
          a device: under "All", selecting archived AND active hives
          together offered Archive but not Restore -- because the filter
          was standing in for what was actually picked, and under "All"
          the filter says nothing about either.

          A mixed selection now offers BOTH, which is safe in both
          directions: batch archive sets is_archived = 1 on rows that
          already have it (a no-op), and batch restore carries
          `AND is_archived = 1`, so it touches only the archived ones. */}
      <BatchActionBar
        selection={selection}
        onDone={load}
        canArchive={selectedHives.some((h) => h.status !== "Archived")}
        canRestore={selectedHives.some((h) => h.status === "Archived")}
      />

      <Card>
        {view === "list" ? (
          /*
           * Whose hives am I looking at (#286).
           *
           * Grouped ONLY when there is something to disambiguate. A
           * beekeeper on their own sees exactly what they saw before --
           * one heading over one list answers a question nobody asked,
           * and that is the overwhelming majority of users.
           *
           * One <HiveTable> definition feeds both paths. Two would be two
           * places to add a column to, and the second one would be
           * forgotten -- which is this codebase's most repeated bug.
           */
          hasMultipleAccounts(hives || []) ? (
            groupByAccount(hives).map((group) => (
              <div key={group.account_user_id ?? "mine"} className="section-spacing">
                <h2 style={{ fontSize: "var(--font-size-md)", margin: "0 0 var(--space-2)" }}>
                  {group.label}
                  <span style={{ color: "var(--color-text-muted)", fontWeight: 400, marginLeft: "var(--space-2)" }}>
                    {group.hives.length} {group.hives.length === 1 ? "hive" : "hives"}
                  </span>
                </h2>
                <HiveTable rows={group.hives} />
              </div>
            ))
          ) : (
            <HiveTable rows={hives} />
          )
        ) : (
          <HiveMap hives={mapHives} />
        )}
      </Card>
    </div>
  );
}
